import React, { useCallback, useState, useEffect, useRef } from 'react';
import { ChevronDown, Download, Network, CircuitBoard, ChevronRight, Minimize2, Clock, DollarSign, Users, TrendingDown, Minus, TrendingUp, AlertTriangle, CheckCircle, Calendar, Target, Zap, Shield, ArrowRight, Star, BarChart3, Settings, Eye, ArrowLeft } from 'lucide-react';
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import mermaid from "mermaid";
import Mermaid from "./Mermaid";

/* ---------- Tabs Components ---------- */
const Tabs = ({ children, defaultValue }) => {
  const [activeTab, setActiveTab] = useState(defaultValue);

  const list = [];
  const content = [];
  React.Children.forEach(children, (child) => {
    if (child.type === TabsList) {
      list.push(React.cloneElement(child, { activeTab, setActiveTab }));
    } else {
      content.push(React.cloneElement(child, { activeTab }));
    }
  });

  return (
    <div className="flex max-w-7xl mx-auto mt-8 bg-white shadow rounded-lg overflow-hidden h-[calc(100vh-200px)]">
      {/* Sidebar */}
      <div className="w-64 bg-gray-50 rounded-xl">
        {list}
      </div>

      {/* Content */}
      <div className="flex-1 p-6 overflow-y-auto">
        {content}
      </div>
    </div>
  );
};

const TabsList = ({ children, activeTab, setActiveTab }) => (
  <div className="flex flex-col w-64 bg-gray-50 overflow-hidden">
    {React.Children.map(children, (child) =>
      React.cloneElement(child, { activeTab, setActiveTab })
    )}
  </div>
);

const TabsTrigger = ({ children, value, activeTab, setActiveTab, icon: Icon }) => {
  const isActive = activeTab === value;

  return (
    <button
      onClick={() => setActiveTab(value)}
      className={`group relative flex items-center px-5 py-3 w-full h-20 text-left text-sm font-medium border-b border-gray-200 transition-all duration-200
        ${isActive 
          ? "bg-white text-red-700 shadow-sm" 
          : "text-gray-500 hover:bg-gray-100 hover:text-gray-600"
        }
      `}
    >
      {/* Active left indicator */}
      {isActive && (
        <span className="absolute left-0 top-0 h-full w-1.5 bg-red-600"></span>
      )}

      {/* Inactive subtle line 
      {!isActive && (
        <span className="absolute left-0 top-3 h-6 w-0.5 bg-gray-500"></span>
      )} */}

      {/* Icon */}
      {Icon && (
        <Icon
          className={`w-5 h-5 mr-3 transition-colors duration-200 ${
            isActive ? "text-red-600" : "text-gray-900 group-hover:text-gray-600"
          }`}
        />
      )}

      {/* Label */}
      <span className={isActive ? "font-semibold" : "font-normal"}>
        {children}
      </span>
    </button>
  );
};

const TabsContent = ({ children, value, activeTab }) => activeTab === value ? <div>{children}</div> : null;

const AIReportTemplate = ({ reportData, onBackToSearch }) => {
  const [expandedSections, setExpandedSections] = useState({});
  const [animatedText, setAnimatedText] = useState('');
  const [currentSection, setCurrentSection] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  useEffect(() => {
    if (reportData) {
      setIsLoading(false);
    }
  }, [reportData]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  function cleanMermaidCode(raw) {
    if (!raw) return "";

    const cleaned = raw
      // Replace \n with actual newlines
      .replace(/\\n/g, "\n")
      // Remove parentheses but keep content inside
      .replace(/\((.*?)\)/g, "$1")
      // Trim extra spaces
      .trim();

    return cleaned;
  }

  const AnimatedText = ({ text, delay = 0 }) => {
    const [displayText, setDisplayText] = useState('');
    
    useEffect(() => {
      let index = 0;
      const timer = setInterval(() => {
        if (index < text.length) {
          setDisplayText(text.slice(0, index + 1));
          index++;
        } else {
          clearInterval(timer);
        }
      }, 20);
      
      return () => clearInterval(timer);
    }, [text]);
    
    return <span>{displayText}</span>;
  };

  const StatusBadge = ({ status }) => {
    const getStatusColor = (status) => {
      switch (status.toLowerCase()) {
        case 'proceed':
        case 'high':
        case 'compliant':
          return 'bg-gradient-to-r from-green-500 to-emerald-500';
        case 'medium':
          return 'bg-gradient-to-r from-yellow-500 to-orange-500';
        case 'low':
        case 'proceed with caution':
          return 'bg-gradient-to-r from-red-500 to-pink-500';
        default:
          return 'bg-gradient-to-r from-blue-500 to-indigo-500';
      }
    };

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-white text-sm font-medium ${getStatusColor(status)} shadow-lg`}>
        {status}
      </span>
    );
  };

  const RiskStatus = ({ status }) => {
    const getStatusColor = (status) => {
      switch (status.toLowerCase()) {
        case 'high':
          return 'bg-gradient-to-r from-red-500 to-pink-500';
        case 'medium':
          return 'bg-gradient-to-r from-yellow-500 to-orange-500';
        case 'low':
        case 'proceed with caution':
          return 'bg-gradient-to-r from-green-500 to-emerald-500';
        default:
          return 'bg-gradient-to-r from-blue-500 to-indigo-500';
      }
    };

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-white text-sm font-medium ${getStatusColor(status)} shadow-lg`}>
        {status}
      </span>
    );
  };

  const MetricCard = ({ icon: Icon, title, value, subtitle, trend, trendDirection = 'up' }) => {
    const getTrendIcon = () => {
      switch(trendDirection) {
        case 'down': return TrendingDown;
        case 'neutral': return Minus;
        default: return TrendingUp;
      }
    };

    const getTrendColor = () => {
      switch(trendDirection) {
        case 'down': return 'text-red-500';
        case 'neutral': return 'text-gray-500';
        default: return 'text-green-500';
      }
    };

    const TrendIcon = getTrendIcon();

    return (
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md hover:border-gray-300 transition-all duration-200">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-lg bg-gradient-to-br report-gradient flex-shrink-0">
            <Icon className="w-5 h-5 text-white" />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between mb-2">
              <h3 className="text-gray-600 text-sm font-medium uppercase tracking-wide">{title}</h3>
              {trend && (
                <div className={`flex items-center ${getTrendColor()} text-sm font-medium`}>
                  <TrendIcon className="w-4 h-4 mr-1" />
                  {trend}
                </div>
              )}
            </div>
            <p className="text-2xl font-bold text-gray-900 mb-1">{value}</p>
            {subtitle && <p className="text-gray-500 text-sm">{subtitle}</p>}
          </div>
        </div>
      </div>
    );
  };

  const WorkflowStep = ({ step, index, totalSteps }) => (
    <div className="relative mb-8">
      {index < totalSteps - 1 && (
        <div className="absolute left-6 top-16 w-0.5 h-32 bg-gradient-to-b report-gradient opacity-30 z-0"></div>
      )}
      <div className="flex items-start space-x-4 relative z-10">
        <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br report-gradient rounded-full flex items-center justify-center text-white font-bold shadow-lg">
          {step.step_number}
        </div>
        <div className="flex-1 bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 shadow-lg border border-gray-100">
          <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
          <p className="text-gray-700 mb-4">{step.description}</p>
          
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                <ArrowRight className="w-4 h-4 mr-2 text-green-500" />
                Key Inputs
              </h4>
              <ul className="space-y-1">
                {step.key_inputs?.map((input, i) => (
                  <li key={i} className="text-sm text-gray-600 flex items-center">
                    <div className="w-2 h-2 bg-blue-400 rounded-full mr-2"></div>
                    {input}
                  </li>
                )) || []}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                <Target className="w-4 h-4 mr-2 text-purple-500" />
                Key Outputs
              </h4>
              <ul className="space-y-1">
                {step.key_outputs?.map((output, i) => (
                  <li key={i} className="text-sm text-gray-600 flex items-center">
                    <div className="w-2 h-2 bg-purple-400 rounded-full mr-2"></div>
                    {output}
                  </li>
                )) || []}
              </ul>
            </div>
          </div>
          
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center text-gray-600">
              <Clock className="w-4 h-4 mr-1" />
              Duration: {step.duration_estimate}
            </div>
            {step.dependencies && (
              <div className="text-orange-600 font-medium">
                ⚠️ {step.dependencies}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Handle missing or incomplete data
  if (!reportData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-red-200 border-t-red-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">No report data available</p>
          {onBackToSearch && (
            <button 
              onClick={onBackToSearch}
              className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors">
              Close
            </button>
          )}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading AI Implementation Report...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 overflow-x-hidden">
      
      {/* Header */}
      <div className="bg-gradient-to-r report-gradient to-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-6 py-12">
          {/* Back button */}
          {onBackToSearch && (
            <button 
              onClick={onBackToSearch}
              className="mb-6 flex items-center text-white/80 hover:text-white transition-colors">
              <Minimize2 strokeWidth={2} />
              
            </button>
          )}
          
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              <AnimatedText text={reportData.report_metadata?.title || "AI Implementation Report"} />
            </h1>
            <p className="text-xl opacity-90 max-w-4xl mx-auto leading-relaxed">
              {reportData.report_metadata?.description || "AI Implementation Analysis"}
            </p>
            <div className="mt-6 flex items-center justify-center space-x-6 text-sm opacity-80">
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                {reportData.report_metadata?.date ? 
                  new Date(reportData.report_metadata.date).toLocaleDateString() : 
                  new Date().toLocaleDateString()
                }
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 overflow-visible">
        {reportData.executive_summary && reportData.budget_analysis && reportData.roi_expectations && (
          <div className="grid md:grid-cols-4 gap-6 mb-8 -mt-16 relative z-15">
            <MetricCard
              icon={CheckCircle}
              title="Recommendation"
              value={reportData.executive_summary.recommendation || "N/A"}
              subtitle="Implementation Status"
            />
            <MetricCard
              icon={DollarSign}
              title="Total Investment"
              value={reportData.budget_analysis.total_annual ? 
                `${reportData.budget_analysis.total_annual.toLocaleString()}` : "N/A"}
              subtitle="Annual Cost"
            />
            <MetricCard
              icon={TrendingUp}
              title="ROI Timeline"
              value={reportData.roi_expectations.payback_period_months ? 
                `${reportData.roi_expectations.payback_period_months} months` : "N/A"}
              subtitle="Payback Period"
            />
            <MetricCard
              icon={Zap}
              title="Success Rate"
              value={reportData.executive_summary.success_probability || "N/A"}
              subtitle="Probability"
            />
          </div>
        )}

        <Tabs defaultValue="implementation">

          <TabsList>
            <TabsTrigger value="implementation" icon={Settings}>Implementation Strategy</TabsTrigger>
            <TabsTrigger value="ai_tools" icon={Zap}>AI Tools Selection</TabsTrigger>
            <TabsTrigger value="chart" icon={Network}>Workflow Chart</TabsTrigger>
            <TabsTrigger value="budget" icon={AlertTriangle}>Budget Analysis</TabsTrigger>
            <TabsTrigger value="risk" icon={TrendingUp}>Risk Assessment</TabsTrigger>
            <TabsTrigger value="roi" icon={Eye}>ROI Expectations</TabsTrigger>
            <TabsTrigger value="recommend" icon={CircuitBoard}>Recommendation</TabsTrigger>
          </TabsList>

          {/* Implementation Strategy */}
          <TabsContent value="implementation">
            {reportData.implementation_strategy && (
              <div className="space-y-8">
                {reportData.implementation_strategy.overview && (
                  <div className="bg-gradient-to-r report-gradient p-6 rounded-2xl text-white">
                    <h3 className="text-lg font-bold mb-3">Strategy Overview</h3>
                    <p className="leading-relaxed opacity-90">{reportData.implementation_strategy.overview}</p>
                  </div>
                )}
                
                {reportData.implementation_strategy.workflow_steps && reportData.implementation_strategy.workflow_steps.length > 0 && (
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-6">Workflow Steps</h3>
                    <div className="space-y-6">
                      {reportData.implementation_strategy.workflow_steps.map((step, index) => (
                        <WorkflowStep 
                          key={step.step_number || index} 
                          step={step} 
                          index={index} 
                          totalSteps={reportData.implementation_strategy.workflow_steps.length}
                        />
                      ))}
                    </div>
                  </div>
                )}
                
                {reportData.implementation_strategy.automation_benefits && (
                  <div className="bg-green-50 border border-green-200 rounded-2xl p-6">
                    <h4 className="font-bold text-green-800 mb-2 flex items-center">
                      <Star className="w-5 h-5 mr-2" />
                      Automation Benefits
                    </h4>
                    <p className="text-green-700">{reportData.implementation_strategy.automation_benefits}</p>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* AI Tools Selection */}
          <TabsContent value="ai_tools">
            {reportData.ai_tools_selection && reportData.ai_tools_selection.length > 0 && (
              <div className="grid gap-6">
                {reportData.ai_tools_selection.map((tool, index) => (
                  <div key={index} className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-gray-200 shadow-lg">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-gray-900">{tool.tool_name}</h3>
                      <div className="flex items-center space-x-2">
                        <StatusBadge status={tool.api_support === "Yes" ? "API Ready" : "Limited API"} />
                      </div>
                    </div>
                    
                    <p className="text-gray-700 mb-4">{tool.description}</p>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-2">Justification</h4>
                        <p className="text-sm text-gray-600 mb-4">{tool.justification}</p>
                        
                        {tool.compliance_summary && (
                          <>
                            <h4 className="font-semibold text-gray-800 mb-2">Compliance</h4>
                            <p className="text-sm text-gray-600">{tool.compliance_summary}</p>
                          </>
                        )}
                      </div>
                      
                      {tool.main_alternative && (
                        <div>
                          <h4 className="font-semibold text-gray-800 mb-2">Alternative Option</h4>
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <p className="font-medium text-yellow-800">{tool.main_alternative.name}</p>
                            <p className="text-sm text-yellow-700 mt-1">{tool.main_alternative.trade_offs}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Tools Sequence Diagram */}
          <TabsContent value="chart">
            {reportData.tools_sequence_diagram && (
              <div className="grid gap-6">
                
                  <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-gray-200 shadow-lg">
                    
                    <p className="text-gray-700 mb-4">{reportData.tools_sequence_diagram.flow_summary}</p>
                    
                    <Mermaid chart={cleanMermaidCode(reportData.tools_sequence_diagram.mermaid_diagram)} />
                    
                  </div>
              
              </div>
            )}
          </TabsContent>

          {/* Budget Analysis */}
          <TabsContent value="budget">
            {reportData.budget_analysis && (
              <>
              <div className="grid lg:grid-cols-3 gap-6 mb-6">
                <MetricCard
                  icon={DollarSign}
                  title="Monthly Cost"
                  value={reportData.budget_analysis.total_monthly ? `$${reportData.budget_analysis.total_monthly}` : "N/A"}
                  subtitle="Recurring"
                />
                <MetricCard
                  icon={BarChart3}
                  title="Annual Cost"
                  value={reportData.budget_analysis.total_annual ? `$${reportData.budget_analysis.total_annual.toLocaleString()}` : "N/A"}
                  subtitle="Total yearly"
                />
                <MetricCard
                  icon={Settings}
                  title="Implementation"
                  value={reportData.budget_analysis.implementation_cost ? `$${reportData.budget_analysis.implementation_cost.toLocaleString()}` : "N/A"}
                  subtitle="One-time setup"
                />
              </div>
              
              {reportData.budget_analysis.tools_costs && reportData.budget_analysis.tools_costs.length > 0 && (
                <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-200">
                  <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                    <h3 className="font-bold text-gray-900">Tool Cost Breakdown</h3>
                  </div>
                  <div className="divide-y divide-gray-200">
                    {reportData.budget_analysis.tools_costs.map((tool, index) => (
                      <div key={index} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold text-gray-900">{tool.tool_name}</h4>
                            <p className="text-sm text-gray-600">{tool.recommended_tier} Plan</p>
                            <p className="text-xs text-gray-500 mt-1">{tool.key_limitations}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-gray-900">${tool.monthly_cost}/mo</p>
                            <p className="text-sm text-gray-600">${tool.annual_cost}/year</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              </>
            )}
          </TabsContent>

          {/* Risk Assessment */}
          <TabsContent value="risk">
            {reportData.risk_assessment && (
              <>
              <div className="grid lg:grid-cols-2 gap-8">
                <div>
                  {reportData.risk_assessment.overall_feasibility && (
                    <div className="flex items-center mb-4">
                      <h3 className="text-lg font-bold text-gray-900 mr-3">Overall Feasibility:</h3>
                      <StatusBadge status={reportData.risk_assessment.overall_feasibility} />
                    </div>
                  )}
                  
                  {reportData.risk_assessment.top_risks && reportData.risk_assessment.top_risks.length > 0 && (
                    <>
                      <h4 className="font-semibold text-gray-800 mb-4">Top Risks</h4>
                      <div className="space-y-4">
                        {reportData.risk_assessment.top_risks.map((risk, index) => (
                          <div key={index} className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="font-medium text-red-800">{risk.risk}</h5>
                              <RiskStatus status={risk.impact} />
                            </div>
                            <p className="text-sm text-red-700"><strong>Mitigation:</strong> {risk.mitigation}</p>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                
                {reportData.risk_assessment.team_requirements && (
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-4">Team Requirements</h4>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                      {reportData.risk_assessment.team_requirements.size && (
                        <div className="flex items-center mb-4">
                          <Users className="w-5 h-5 text-blue-600 mr-2" />
                          <span className="font-medium text-blue-800">Team Size: {reportData.risk_assessment.team_requirements.size} people</span>
                        </div>
                      )}
                      
                      {reportData.risk_assessment.team_requirements.key_skills && reportData.risk_assessment.team_requirements.key_skills.length > 0 && (
                        <div className="mb-4">
                          <p className="font-medium text-blue-800 mb-2">Required Skills:</p>
                          <div className="space-y-1">
                            {reportData.risk_assessment.team_requirements.key_skills.map((skill, index) => (
                              <div key={index} className="flex items-center text-sm text-blue-700">
                                <CheckCircle className="w-4 h-4 mr-2" />
                                {skill}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {reportData.risk_assessment.team_requirements.time_commitment && (
                        <div className="flex items-center">
                          <Clock className="w-5 h-5 text-blue-600 mr-2" />
                          <span className="text-blue-700">{reportData.risk_assessment.team_requirements.time_commitment}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              </>
            )}
          </TabsContent>

          {/* ROI Expectations */}
          <TabsContent value="roi">
            {reportData.roi_expectations && (
              <>
              <div className="grid lg:grid-cols-3 gap-6 mb-6">
                <MetricCard
                  icon={Calendar}
                  title="Payback Period"
                  value={reportData.roi_expectations.payback_period_months ? 
                    `${reportData.roi_expectations.payback_period_months} months` : "N/A"}
                  subtitle="Time to ROI"
                />
                <MetricCard
                  icon={DollarSign}
                  title="Annual Savings"
                  value={reportData.roi_expectations.annual_savings ? 
                    `${reportData.roi_expectations.annual_savings.toLocaleString()}` : "N/A"}
                  subtitle="Cost reduction"
                />
                <MetricCard
                  icon={Zap}
                  title="Efficiency Gain"
                  value={reportData.roi_expectations.efficiency_improvement || "N/A"}
                  subtitle="Performance boost"
                />
              </div>
              
              {reportData.roi_expectations.key_kpis && reportData.roi_expectations.key_kpis.length > 0 && (
                <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-200">
                  <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                    <h3 className="font-bold text-gray-900">Key Performance Indicators</h3>
                  </div>
                  <div className="divide-y divide-gray-200">
                    {reportData.roi_expectations.key_kpis.map((kpi, index) => (
                      <div key={index} className="px-6 py-4">
                        <h4 className="font-semibold text-gray-900 mb-2">{kpi.metric}</h4>
                        <div className="flex items-center justify-between">
                          <div className="text-center">
                            <p className="text-sm text-gray-600">Current</p>
                            <p className="text-lg font-bold text-red-600">{kpi.current}</p>
                          </div>
                          <ArrowRight className="w-6 h-6 text-gray-400" />
                          <div className="text-center">
                            <p className="text-sm text-gray-600">Target</p>
                            <p className="text-lg font-bold text-green-600">{kpi.target}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              </>
            )}
          </TabsContent>

          {/* Executive Summary */}
          <TabsContent value="recommend">
            {reportData.executive_summary && (
                <div className="grid lg:grid-cols-2 gap-8">
                  <div>
                    {reportData.executive_summary.recommendation && (
                      <div className="flex items-center mb-4">
                        <h3 className="text-lg font-bold text-gray-900 mr-3">Recommendation:</h3>
                        <StatusBadge status={reportData.executive_summary.recommendation} />
                      </div>
                    )}
                    
                    {reportData.executive_summary.key_benefits && reportData.executive_summary.key_benefits.length > 0 && (
                      <div className="mb-6">
                        <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                          <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                          Key Benefits
                        </h4>
                        <div className="space-y-2">
                          {reportData.executive_summary.key_benefits.map((benefit, index) => (
                            <div key={index} className="flex items-start">
                              <div className="w-2 h-2 bg-green-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                              <p className="text-gray-700">{benefit}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {reportData.executive_summary.main_challenges && reportData.executive_summary.main_challenges.length > 0 && (
                      <div className="mb-6">
                        <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                          <AlertTriangle className="w-5 h-5 text-orange-500 mr-2" />
                          Main Challenges
                        </h4>
                        <div className="space-y-2">
                          {reportData.executive_summary.main_challenges.map((challenge, index) => (
                            <div key={index} className="flex items-start">
                              <div className="w-2 h-2 bg-orange-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                              <p className="text-gray-700">{challenge}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {reportData.executive_summary.success_probability && (
                      <div className="flex items-center">
                        <h4 className="font-semibold text-gray-800 mr-3">Success Probability:</h4>
                        <StatusBadge status={reportData.executive_summary.success_probability} />
                      </div>
                    )}
                  </div>
                  
                  {reportData.executive_summary.next_steps && reportData.executive_summary.next_steps.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-4 flex items-center">
                        <Target className="w-5 h-5 text-blue-500 mr-2" />
                        Next Steps
                      </h4>
                      <div className="space-y-4">
                        {reportData.executive_summary.next_steps.map((step, index) => (
                          <div key={index} className="bg-blue-50 border border-blue-200 rounded-lg p-4 hover:bg-blue-100 transition-colors">
                            <div className="flex items-start">
                              <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0 mt-0.5">
                                {index + 1}
                              </div>
                              <p className="text-blue-800 font-medium">{step}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 overflow-visible">{/* Executive Summary Cards */}
        {/* Footer CTA */}
        <div className="bg-gradient-to-r report-gradient to-indigo-700 rounded-2xl p-8 text-white text-center shadow-2xl">
          <h2 className="text-2xl font-bold mb-4">Ready to Transform Your Workflow?</h2>
          <p className="text-lg opacity-90 mb-6 max-w-2xl mx-auto">
            This AI implementation strategy provides a clear roadmap to automate your content creation process and achieve significant efficiency gains.
          </p>
        </div>

      </div>
    </div>
  );
};

export default AIReportTemplate;